
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, LogIn, Stars, CloudMoon, Moon, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { FeatureBadge } from './FeatureBadge';
import { useIsMobile } from '@/hooks/use-mobile';
import { AppDownloadButtons } from '../AppDownloadButtons';

export function HeroContent() {
  const isMobile = useIsMobile();
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="flex flex-col space-y-4 md:space-y-6"
    >
      {/* Logo */}
      <div className="mb-4 md:mb-6">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="inline-block bg-gradient-to-r from-[#308CFD] to-[#4CCEFD] text-white px-4 py-2 md:px-5 md:py-3 rounded-lg shadow-lg"
        >
          <h3 className="text-lg md:text-xl font-bold tracking-tight">SleepNest</h3>
        </motion.div>
      </div>
      
      {/* Main Headline */}
      <motion.h1 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.7 }}
        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900"
      >
        The Smartest Way to Identify Sleep Challenges in Children
      </motion.h1>
      
      {/* Subheading */}
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.7 }}
        className="text-lg md:text-xl text-gray-700 max-w-xl"
      >
        Get clear, data-driven insights to support your clients—faster than ever.
      </motion.p>
      
      {/* CTA Buttons */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.7 }}
        className="flex flex-col sm:flex-row gap-4 pt-2 md:pt-4"
      >
        <Button
          asChild
          size={isMobile ? "default" : "lg"}
          className="bg-[#308CFD] hover:bg-[#2563EB] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all border-2 border-[#308CFD] relative w-full sm:w-auto"
        >
          <Link to="/specialist-registration" className="flex items-center justify-center">
            <span className="relative z-10">Start Your Free Trial</span> <ArrowRight className="ml-2 h-4 w-4 relative z-10" />
            <span className="absolute inset-0 bg-gradient-to-r from-[#308CFD] to-[#4CCEFD] rounded-lg opacity-100"></span>
          </Link>
        </Button>
        
        {/* Login Button with Animation */}
        <motion.div
          whileHover={{ 
            scale: 1.05,
            transition: { duration: 0.2 }
          }}
          whileTap={{ scale: 0.98 }}
          className="relative w-full sm:w-auto"
        >
          <motion.span
            className="absolute -inset-1 rounded-lg bg-gradient-to-r from-sky-100 to-blue-100 opacity-70 blur-sm"
            animate={{ 
              opacity: [0.5, 0.8, 0.5],
              scale: [0.98, 1.01, 0.98]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 3,
              ease: "easeInOut" 
            }}
          />
          <Button
            asChild
            size={isMobile ? "default" : "lg"}
            variant="secondary"
            className="relative bg-white text-[#308CFD] border border-sky-100 hover:border-sky-200 hover:bg-sky-50 rounded-lg shadow-sm z-10 w-full sm:w-auto"
          >
            <Link to="/roleselection" className="flex items-center justify-center">
              <LogIn className="mr-2 h-5 w-5" /> 
              <span className="font-medium">Log In</span>
            </Link>
          </Button>
        </motion.div>
      </motion.div>
      
      {/* Mobile App Download Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.7 }}
        className="pt-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <Smartphone className="h-4 w-4 text-gray-600" />
          <p className="text-sm font-medium text-gray-600">Mobile App for Parents & Caregivers</p>
        </div>
        <AppDownloadButtons />
      </motion.div>
      
      {/* Trust Badges */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.7 }}
        className="flex flex-wrap gap-3 md:gap-6 pt-6 md:pt-10"
      >
        <FeatureBadge icon={<Stars className="h-4 w-4 md:h-5 md:w-5" />} text="AI-Powered Analysis" />
        <FeatureBadge icon={<CloudMoon className="h-4 w-4 md:h-5 md:w-5" />} text="Sleep Expert Approved" />
        <FeatureBadge icon={<Moon className="h-4 w-4 md:h-5 md:w-5" />} text="Evidence-Based Recommendations" />
      </motion.div>
    </motion.div>
  );
}
